import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { IUser } from '../../lib/interfaces';
import { reportError } from '../../lib/sentry';
import { RootState } from '../../store/types/sharedTypes';
import {
  getPatientVideoToken,
  VideoToken,
} from '../../lib/services/videoService';
import FlashSnackbar from '../common/FlashSnackbar';
import VideoVisitButton from './components/VideoVisitButton';
import VideoCall from './VideoCall';

// The PPoC is staff only: the "patient" is a staff member with isAdmin on a
// patient account, so real patients never see the button (EASE2-603). Only
// staff can start the call from their side.
// ENABLE_VIDEO_MEETINGS is baked in at build time, per environment in
// bitbucket-pipelines.yml.
export const canStartVideoVisit = (
  patient: IUser,
  sessionUser?: IUser | null
): boolean =>
  process.env.ENABLE_VIDEO_MEETINGS === 'true' &&
  !!sessionUser?.isAdmin &&
  patient.isPatient &&
  patient.isAdmin;

// api-service answers 404 on every video route while ENABLE_VIDEO_MEETINGS is
// off in that environment.
export const tokenErrorKey = (err: unknown): string => {
  if (isAxiosError(err) && err.response?.status === 404) {
    return 'videoVisit.error.unavailable';
  }
  return 'videoVisit.error.token';
};

type Props = {
  patient: IUser | null | undefined;
};

const VideoCallContainer = ({ patient }: Props) => {
  const { t } = useTranslation();
  const { sessionUser } = useSelector((state: RootState) => state.general);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState<VideoToken | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  if (!patient || !canStartVideoVisit(patient, sessionUser)) return null;

  // The call screen opens only once api-service has issued a token; a failure
  // stays on the patient page as a toast. Try again in the call fetches a new
  // token too, since the old one may have outlived its one hour lifetime.
  const fetchCredentials = async () => {
    if (loading) return;
    setLoading(true);
    setErrorKey(null);
    try {
      const issued = await getPatientVideoToken(patient._id);
      setCredentials(issued);
    } catch (err) {
      reportError(err, { tags: { feature: 'video' } });
      setErrorKey(tokenErrorKey(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <VideoVisitButton loading={loading} onClick={fetchCredentials} />

      <FlashSnackbar
        open={errorKey !== null}
        msg={errorKey ? t(errorKey) : ''}
        severity="error"
        onClose={() => setErrorKey(null)}
      />

      <Dialog fullScreen open={credentials !== null} disableEscapeKeyDown>
        {credentials && (
          <VideoCall
            key={credentials.token}
            credentials={credentials}
            patient={patient}
            onRetry={fetchCredentials}
            onLeave={() => setCredentials(null)}
          />
        )}
      </Dialog>
    </>
  );
};

export default VideoCallContainer;
